import React, { useMemo } from "react";
import {
  useFilterPositions,
  useFilterOrders,
} from "@/vybe-trader-client/trade-history/hooks/useFilterTrades";
import { useFilterStore } from "@/vybe-trader-client/trade-history/stores/useFilterStore";
import { computeHistoryPositionsSummary } from "@/vybe-trader-client/trade-history/historySummary";
import { TabType } from "../HistoryScreen";

interface SummaryProps {
  activeTab: TabType;
}

function formatMoney(value: number): string {
  return value.toFixed(2);
}

function SummaryCell({
  label,
  value,
  valueClassName = "text-foreground",
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-2 text-xs">
      <span className="truncate text-muted-foreground">{label}</span>
      <span className={`shrink-0 tabular-nums ${valueClassName}`}>{value}</span>
    </div>
  );
}

export default function Summary({ activeTab }: SummaryProps) {
  const selectedSymbol = useFilterStore((state) => state.selectedSymbol);
  const dateFilter = useFilterStore((state) => state.dateFilter);
  const customDateFrom = useFilterStore((state) => state.customDateFrom);
  const customDateTo = useFilterStore((state) => state.customDateTo);

  const positions = useFilterPositions({
    symbol: selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
  });

  const orders = useFilterOrders({
    symbol: selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
  });

  const historySummary = useMemo(() => {
    if (activeTab !== "positions" && activeTab !== "deals") {
      return null;
    }
    return computeHistoryPositionsSummary(positions);
  }, [activeTab, positions]);

  const ordersSummary = useMemo(() => {
    if (activeTab !== "orders") {
      return { total: 0, filled: 0, canceled: 0 };
    }

    return {
      total: orders.length,
      filled: orders.filter((o) => o.state === 4).length,
      canceled: orders.filter((o) => o.state === 2).length,
    };
  }, [activeTab, orders]);

  return (
    <div className="border-t border-border px-3 py-2">
      {historySummary && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <SummaryCell
            label="Deposit"
            value={formatMoney(historySummary.deposits)}
            valueClassName={
              historySummary.deposits > 0 ? "text-blue-400" : "text-foreground"
            }
          />
          <SummaryCell
            label="Withdrawal"
            value={`-${formatMoney(historySummary.withdrawals)}`}
            valueClassName={
              historySummary.withdrawals > 0 ? "text-red-400" : "text-foreground"
            }
          />
          <SummaryCell
            label="Net P&L"
            value={formatMoney(historySummary.netPnl)}
            valueClassName={
              historySummary.netPnl >= 0 ? "text-blue-400" : "text-red-400"
            }
          />
          <SummaryCell
            label="Profit"
            value={formatMoney(historySummary.tradingProfit)}
            valueClassName={
              historySummary.tradingProfit >= 0
                ? "text-blue-400"
                : "text-red-400"
            }
          />
          <SummaryCell
            label="Swap"
            value={formatMoney(historySummary.swap)}
            valueClassName={
              historySummary.swap >= 0 ? "text-blue-400" : "text-red-400"
            }
          />
          <SummaryCell
            label="Commission"
            value={formatMoney(historySummary.commission)}
            valueClassName={
              historySummary.commission >= 0
                ? "text-blue-400"
                : "text-red-400"
            }
          />
        </div>
      )}

      {activeTab === "orders" && (
        <div className="grid grid-cols-3 gap-x-3 gap-y-1">
          <SummaryCell label="Total" value={ordersSummary.total} />
          <SummaryCell
            label="Filled"
            value={ordersSummary.filled}
            valueClassName="text-blue-400"
          />
          <SummaryCell
            label="Canceled"
            value={ordersSummary.canceled}
            valueClassName="text-red-400"
          />
        </div>
      )}
    </div>
  );
}
